import { Dispatch, SetStateAction } from "react";

import { Col, Container, Row } from "react-bootstrap";
import {
  useLegislationAgenciesQuery,
  useLegislationNormsQuery,
  useLegislationOptionsQuery,
} from "../../../../services";
import { PDLTypography } from "../../dna";
import { CodeSearchFilter } from "../_codeSearchFilter";
import { DetailedSearchFilter } from "../_detailedSearchFilter";
import LexLoading from "../_lexLoading";
import { OnPeriodChangePayload } from "../_periodSelector";
import "./style.scss";

type SearchMode = "code" | "detailed" | null;

export interface LegislationFilterValues {
  searchMode: SearchMode;
  // Code search
  selectedCode: string;
  codeArticle: string;
  // Detailed search
  normType: string;
  agency: string;
  number: string;
  article: string;
}

export const legislationFiltersInitialValue: LegislationFilterValues = {
  searchMode: null,
  selectedCode: "",
  codeArticle: "",
  normType: "",
  agency: "",
  number: "",
  article: "",
};

interface LegislationFilterOptionsProps {
  legislationFilter: LegislationFilterValues;
  setLegislationFilter: Dispatch<SetStateAction<LegislationFilterValues>>;
  onPeriodChange: (data: OnPeriodChangePayload) => void;
  period: OnPeriodChangePayload;
}

export function LegislationFilterOptions({
  legislationFilter,
  setLegislationFilter,
  period,
  onPeriodChange,
}: LegislationFilterOptionsProps) {
  const { error: optionsError, isPending: optionsLoading } =
    useLegislationOptionsQuery();

  const { error: normsError, isPending: normsLoading } =
    useLegislationNormsQuery();

  const { error: agencysError, isPending: agencysLoading } =
    useLegislationAgenciesQuery();

  const updateValues = (patch: Partial<LegislationFilterValues>) => {
    setLegislationFilter((prev) => ({
      ...prev,
      ...patch,
    }));
  };

  const handleChangeSearchMode = (searchMode: SearchMode) => {
    if (searchMode === "code") {
      updateValues({
        searchMode: "code",
        normType: "",
        agency: "",
        number: "",
        article: "",
      });
      onPeriodChange({ period: "always", endDate: null, startDate: null });
    } else {
      updateValues({
        searchMode: "detailed",
        selectedCode: "",
        codeArticle: "",
      });
    }
  };

  const anyLoading = optionsLoading || normsLoading || agencysLoading;
  const anyError = optionsError || normsError || agencysError;

  if (anyError) {
    return (
      <Container>
        <Row>
          <Col>
            <div className="text-center mt-5">
              <PDLTypography size="xl" variant="primary" weight="medium">
                Parece que não possível buscar os filtros
              </PDLTypography>
              <p className="text-muted">
                Se for urgente, fale com a gente. Teremos prazer em ajudar.
              </p>
            </div>
          </Col>
        </Row>
      </Container>
    );
  }

  if (anyLoading)
    return (
      <Container>
        <Row>
          <Col>
            <div className="content-loading">
              <LexLoading size={100} customText="Buscando filtros..." />
            </div>
          </Col>
        </Row>
      </Container>
    );

  return (
    <>
      <CodeSearchFilter
        isActive={legislationFilter.searchMode === "code"}
        isDisabled={legislationFilter.searchMode === "detailed"}
        selectedCode={legislationFilter.selectedCode}
        article={legislationFilter.codeArticle}
        onActivate={() => handleChangeSearchMode("code")}
        onCodeChange={(v) => updateValues({ selectedCode: v || "" })}
        onArticleChange={(v) => updateValues({ codeArticle: v || "" })}
      />

      <div className="legislation-filter-options-divider">
        <span className="legislation-filter-options-divider-text">ou</span>
      </div>

      <DetailedSearchFilter
        isActive={legislationFilter.searchMode === "detailed"}
        isDisabled={legislationFilter.searchMode === "code"}
        normType={legislationFilter.normType}
        agency={legislationFilter.agency}
        number={legislationFilter.number}
        article={legislationFilter.article}
        onPeriodChange={onPeriodChange}
        period={period}
        onActivate={() => handleChangeSearchMode("detailed")}
        onNormTypeChange={(v) => updateValues({ normType: v })}
        onAgencyChange={(v) => updateValues({ agency: v })}
        onNumberChange={(v) => updateValues({ number: v })}
        onArticleChange={(v) => updateValues({ article: v })}
      />
    </>
  );
}
